import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

interface NearbyOfferCardProps extends PressableAnalyticsProps {
  /** Square shaped icon to be displayed on the card*/
  iconUrl?: string;
  /** Title to be displayed on the card */
  titleText: string;
  /** Used to display the caption at the bottom of card */
  subtitleText?: Maybe<string>;
  /** Determines if the card is in a selected state */
  isSelected?: boolean;
  /** Disable the card interaction */
  isDisabled?: boolean;
  /** Function used to handle the card press action */
  onCardPress: () => void;
}

const titleTextLineLimit = 1;
const captionTextLineLimit = 1;

export const NearbyOfferCard = ({
  iconUrl,
  titleText,
  subtitleText,
  isSelected = false,
  isDisabled = false,
  onCardPress,
  analyticsId = '',
  customAttributes = null,
}: NearbyOfferCardProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const cardPressAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  const handleCardPress = () => {
    cardPressAnalytics();
    onCardPress();
  };

  return (
    <Pressable
      onPress={handleCardPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      role="button"
      accessible
      style={[
        styles.container,
        isFocused && styles.focus,
        isHovered && styles.hover,
        isPressed && styles.press,
        isSelected && styles.selected,
        isDisabled && styles.disabled,
      ]}
      testID={`${titleText} Tile`}
    >
      <View style={styles.mainContainer}>
        <IconAvatar
          variant="MD"
          backgroundColor={theme.color.background.tertiary}
          defaultPlaceholderIcon="STORE"
          iconKey={iconUrl ? undefined : 'STORE'}
          imageSource={iconUrl ? { uri: iconUrl } : undefined}
          iconAccessibilityLabel="offer-logo-image"
        />

        <View style={styles.textContainer}>
          <BodySmBoldText
            style={(styles.title, isDisabled && styles.disabledTitle)}
            numberOfLines={titleTextLineLimit}
          >
            {titleText}
          </BodySmBoldText>
          {subtitleText ? (
            <BodySmText
              style={(styles.caption, isDisabled && styles.disabledCaption)}
              numberOfLines={captionTextLineLimit}
            >
              {subtitleText}
            </BodySmText>
          ) : null}
        </View>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  caption: {
    color: theme.color.text.secondary,
  },
  container: {
    backgroundColor: theme.color.interactive.secondary,
    borderColor: theme.color.background.primary,
    borderRadius: 4,
    borderWidth: 1,
    height: 72,
    width: 172,
  },
  disabled: {
    backgroundColor: theme.color.interactive.disabled.background,
    borderColor: theme.color.border.primarySolid,
  },
  disabledCaption: {
    color: theme.color.text.muted,
  },
  disabledTitle: {
    color: theme.color.text.muted,
  },
  focus: {
    backgroundColor: theme.color.interactive.secondary,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  mainContainer: {
    flex: 1,
    flexDirection: 'row',
    flexShrink: 1,
    gap: theme.spacing.s,
    padding: 12,
    alignItems: 'center',
  },
  press: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
  selected: {
    borderColor: theme.color.expressive.varoPurple,
  },
  textContainer: {
    flexShrink: 1,
    justifyContent: 'center',
  },
  title: {
    color: theme.color.text.primary,
  },
});

export default NearbyOfferCard;
